import React, { useState, useMemo } from 'react';
import patternsRadarService from '../../../services/patternsRadarService';
import DoubleScrollTable from './DoubleScrollTable';
import {
  formatPercent,
  formatMultiplier,
  formatDecimal,
  toNum,
} from '../patternsRadarUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHistory,
  faPlay,
  faSpinner,
  faExclamationTriangle,
  faChartLine,
  faTrophy,
  faPercentage,
  faCrosshairs,
} from '@fortawesome/free-solid-svg-icons';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const StrategyBacktest = ({ initialQuery = '' }) => {
  const [source, setSource] = useState(
    initialQuery || 'rsi(14) crossed above 30 within 3 bars'
  );
  const [universe, setUniverse] = useState(500);
  const [sessions, setSessions] = useState(250);
  const [entryMode, setEntryMode] = useState('close');
  const [benchmark, setBenchmark] = useState('NIFTY 50');
  const [detailHorizon, setDetailHorizon] = useState(20);

  // Optional Risk Simulation
  const [enableTradeSim, setEnableTradeSim] = useState(false);
  const [stopLossPct, setStopLossPct] = useState(5);
  const [targetPct, setTargetPct] = useState(10);

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [errorInfo, setErrorInfo] = useState(null);

  // Table Column Filters (CRM Theme)
  const [colFilterMonth, setColFilterMonth] = useState('');
  const [colFilterWinRate, setColFilterWinRate] = useState('');
  const [colFilterMean, setColFilterMean] = useState('');
  const [colFilterExcess, setColFilterExcess] = useState('');

  const hasActiveFilters = Boolean(
    colFilterMonth || colFilterWinRate || colFilterMean || colFilterExcess
  );

  const clearFilters = () => {
    setColFilterMonth('');
    setColFilterWinRate('');
    setColFilterMean('');
    setColFilterExcess('');
  };

  const filteredMonthly = useMemo(() => {
    if (!result?.monthly) return [];
    return result.monthly.filter((m) => {
      if (
        colFilterMonth &&
        !m.month?.toLowerCase().includes(colFilterMonth.toLowerCase().trim())
      ) {
        return false;
      }
      if (colFilterWinRate === 'win50' && (toNum(m.winRatePct) ?? 0) < 50) {
        return false;
      }
      if (colFilterWinRate === 'loss50' && (toNum(m.winRatePct) ?? 0) >= 50) {
        return false;
      }
      if (colFilterMean === 'pos' && (toNum(m.meanPct) ?? 0) <= 0) {
        return false;
      }
      if (colFilterMean === 'neg' && (toNum(m.meanPct) ?? 0) >= 0) {
        return false;
      }
      if (colFilterExcess === 'pos' && (toNum(m.meanExcessPct) ?? 0) <= 0) {
        return false;
      }
      if (colFilterExcess === 'neg' && (toNum(m.meanExcessPct) ?? 0) >= 0) {
        return false;
      }
      return true;
    });
  }, [result?.monthly, colFilterMonth, colFilterWinRate, colFilterMean, colFilterExcess]);

  const handleRunBacktest = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    setErrorInfo(null);

    try {
      const payload = {
        source: source.trim(),
        universe: Number(universe),
        sessions: Number(sessions),
        horizons: [1, 5, 20],
        entryMode,
        benchmark,
        detailHorizon: Number(detailHorizon),
      };

      if (enableTradeSim) {
        payload.stopLossPct = Number(stopLossPct);
        payload.targetPct = Number(targetPct);
      }

      const data = await patternsRadarService.runHitRate(payload);

      if (data.ok === false) {
        setErrorInfo({
          type: 'compiler',
          message: data.error || 'Compiler rejected Sift query',
          hint: data.hint,
        });
        setResult(null);
      } else {
        setResult(data);
      }
    } catch (err) {
      setErrorInfo({
        type: 'network',
        message:
          err.response?.data?.detail ||
          err.response?.data?.title ||
          err.message ||
          'Failed to run strategy backtest',
      });
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const primaryBucket = result?.buckets?.find(
    (b) => b.horizon === Number(detailHorizon)
  ) || result?.buckets?.[0];

  return (
    <div>
      {/* Configuration Panel */}
      <div className="panel panel-default with-rounded-corner with-shadow mb-3">
        <div className="panel-heading border-0">
          <span className="panel-title font-weight-bold">
            <FontAwesomeIcon icon={faHistory} className="text-primary me-2" />
            Historical Strategy Hit-Rate & Replay Engine
          </span>
        </div>

        <div className="panel-body">
          <div className="mb-3">
            <label className="form-label small font-weight-bold text-muted">
              Sift Trading Signal Query
            </label>
            <textarea
              className="pr-query-box"
              value={source}
              onChange={(e) => setSource(e.target.value)}
              placeholder="e.g. rsi(14) crossed above 30 within 3 bars and close > sma(50)"
            />
          </div>

          {errorInfo && (
            <div className="alert alert-danger py-2 px-3 small d-flex align-items-center mb-3">
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="me-2 text-danger fs-5"
              />
              <div>
                <strong>Replay Refused:</strong> {errorInfo.message}
                {errorInfo.hint && (
                  <span className="ms-2 badge bg-warning text-dark">
                    Hint: {errorInfo.hint}
                  </span>
                )}
              </div>
            </div>
          )}

          <div className="row g-2 align-items-end">
            <div className="col-md-2">
              <label className="form-label small text-muted mb-1 font-weight-bold">
                Test Sessions
              </label>
              <select
                className="form-control form-control-sm"
                value={sessions}
                onChange={(e) => setSessions(e.target.value)}
              >
                <option value="125">125 Sessions (~6 Months)</option>
                <option value="250">250 Sessions (~1 Year)</option>
                <option value="500">500 Sessions (~2 Years)</option>
                <option value="1250">1,250 Sessions (~5 Years)</option>
              </select>
            </div>

            <div className="col-md-2">
              <label className="form-label small text-muted mb-1 font-weight-bold">
                Entry Timing
              </label>
              <select
                className="form-control form-control-sm"
                value={entryMode}
                onChange={(e) => setEntryMode(e.target.value)}
              >
                <option value="close">At Close (Same Day)</option>
                <option value="nextOpen">Next Day Open</option>
              </select>
            </div>

            <div className="col-md-2">
              <label className="form-label small text-muted mb-1 font-weight-bold">
                Benchmark
              </label>
              <select
                className="form-control form-control-sm"
                value={benchmark}
                onChange={(e) => setBenchmark(e.target.value)}
              >
                <option value="NIFTY 50">NIFTY 50</option>
                <option value="NIFTY 100">NIFTY 100</option>
                <option value="NIFTY 500">NIFTY 500</option>
                <option value="NIFTY BANK">NIFTY BANK</option>
                <option value="none">No Benchmark</option>
              </select>
            </div>

            <div className="col-md-2">
              <label className="form-label small text-muted mb-1 font-weight-bold">
                Primary Horizon
              </label>
              <select
                className="form-control form-control-sm"
                value={detailHorizon}
                onChange={(e) => setDetailHorizon(e.target.value)}
              >
                <option value="1">1 Day</option>
                <option value="5">5 Days (1 Week)</option>
                <option value="20">20 Days (~1 Month)</option>
              </select>
            </div>

            <div className="col-md-2">
              <div className="form-check mb-1">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="tradeSimCheck"
                  checked={enableTradeSim}
                  onChange={(e) => setEnableTradeSim(e.target.checked)}
                />
                <label
                  className="form-check-label small font-weight-bold"
                  htmlFor="tradeSimCheck"
                >
                  SL / TP Simulation
                </label>
              </div>
              {enableTradeSim && (
                <div className="d-flex gap-1">
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    placeholder="SL %"
                    value={stopLossPct}
                    onChange={(e) => setStopLossPct(e.target.value)}
                    title="Stop Loss %"
                  />
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    placeholder="TP %"
                    value={targetPct}
                    onChange={(e) => setTargetPct(e.target.value)}
                    title="Target %"
                  />
                </div>
              )}
            </div>

            <div className="col-md-2 text-end">
              <button
                type="button"
                className="btn btn-primary btn-sm px-4 font-weight-bold w-100"
                onClick={handleRunBacktest}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
                    Simulating...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faPlay} className="me-1" />
                    Run Replay
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      {result && primaryBucket && (
        <div className="row g-2 mb-3">
          <div className="col-md-3">
            <div className="pr-kpi-card text-center">
              <div className="pr-kpi-title">
                <FontAwesomeIcon icon={faTrophy} className="text-warning me-1" />
                Win Rate ({primaryBucket.horizon}D Horizon)
              </div>
              <div
                className={`pr-kpi-value ${
                  (toNum(primaryBucket.winRatePct) ?? 0) >= 50 ? 'pr-positive' : 'pr-negative'
                }`}
              >
                {formatPercent(primaryBucket.winRatePct, { showPlus: false })}
              </div>
              <small className="text-muted">
                {primaryBucket.n} Sample Trades Tested
              </small>
            </div>
          </div>

          <div className="col-md-3">
            <div className="pr-kpi-card text-center">
              <div className="pr-kpi-title">
                <FontAwesomeIcon
                  icon={faPercentage}
                  className="text-primary me-1"
                />
                Payoff Ratio (Win / Loss)
              </div>
              <div className="pr-kpi-value">
                {formatMultiplier(primaryBucket.payoff)}
              </div>
              <small className="text-muted">
                Avg Win: {formatPercent(primaryBucket.avgWinPct, { showPlus: true })} | Avg Loss:{' '}
                {formatPercent(primaryBucket.avgLossPct, { showPlus: false })}
              </small>
            </div>
          </div>

          <div className="col-md-3">
            <div className="pr-kpi-card text-center">
              <div className="pr-kpi-title">
                <FontAwesomeIcon icon={faChartLine} className="text-info me-1" />
                Excess Return vs {result.benchmark}
              </div>
              <div
                className={`pr-kpi-value ${
                  (toNum(primaryBucket.benchmark?.meanExcessPct) ?? 0) >= 0
                    ? 'pr-positive'
                    : 'pr-negative'
                }`}
              >
                {formatPercent(primaryBucket.benchmark?.meanExcessPct, { showPlus: true })}
              </div>
              <small className="text-muted">
                Beat Benchmark: {formatPercent(primaryBucket.benchmark?.beatBenchmarkPct, { showPlus: false })}
              </small>
            </div>
          </div>

          <div className="col-md-3">
            <div className="pr-kpi-card text-center">
              <div className="pr-kpi-title">
                <FontAwesomeIcon
                  icon={faCrosshairs}
                  className="text-success me-1"
                />
                Total Signals Generated
              </div>
              <div className="pr-kpi-value text-dark">
                {result.totalSignals}
              </div>
              <small className="text-muted">
                ~{formatDecimal(result.signalsPerSession, { decimals: 1 })} Signals per session
              </small>
            </div>
          </div>
        </div>
      )}

      {/* Equity Curve Visualizer */}
      {result?.equityCurve?.points && (
        <div className="panel panel-default with-rounded-corner with-shadow mb-3">
          <div className="panel-heading border-0 d-flex justify-content-between align-items-center">
            <span className="panel-title font-weight-bold">
              Simulated Strategy Equity Curve vs {result.benchmark}
            </span>
            <div className="small text-muted">
              Max Drawdown: <strong>{formatPercent(result.equityCurve.maxDrawdownPct, { showPlus: false })}</strong>{' '}
              | Benchmark DD:{' '}
              <strong>{formatPercent(result.equityCurve.benchMaxDrawdownPct, { showPlus: false })}</strong>
            </div>
          </div>
          <div className="panel-body">
            <div style={{ width: '100%', height: 260 }}>
              <ResponsiveContainer>
                <LineChart data={result.equityCurve.points}>
                  <XAxis dataKey="d" stroke="#94a3b8" fontSize={11} />
                  <YAxis stroke="#94a3b8" fontSize={11} domain={['auto', 'auto']} />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="scan"
                    name="Sift Strategy"
                    stroke="#127bbc"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="bench"
                    name={`${result.benchmark} Benchmark`}
                    stroke="#94a3b8"
                    strokeWidth={1.5}
                    strokeDasharray="4 4"
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Monthly Breakdown Table */}
      {result?.monthly && result.monthly.length > 0 && (
        <div className="panel panel-default with-rounded-corner with-shadow">
          <div className="panel-heading border-0">
            <span className="panel-title font-weight-bold">
              Monthly Signal Consistency Matrix
            </span>
          </div>
          <div className="panel-body p-0">
            <DoubleScrollTable dependencies={[filteredMonthly]}>
              <table className="table table-hover table-condensed bg-white m-b-0 sticky-table pr-table">
                <thead>
                  <tr className="bg-light">
                    <th style={{ minWidth: '140px' }}>Month</th>
                    <th style={{ minWidth: '100px' }} className="text-end">Signals</th>
                    <th style={{ minWidth: '130px' }} className="text-end">Win Rate %</th>
                    <th style={{ minWidth: '140px' }} className="text-end">Mean Return %</th>
                    <th style={{ minWidth: '180px' }} className="text-end">Excess Return vs Benchmark</th>
                  </tr>
                  <tr>
                    <th>
                      <input
                        type="text"
                        placeholder="Search Month..."
                        value={colFilterMonth}
                        onChange={(e) => setColFilterMonth(e.target.value)}
                      />
                    </th>
                    <th></th>
                    <th>
                      <select
                        value={colFilterWinRate}
                        onChange={(e) => setColFilterWinRate(e.target.value)}
                      >
                        <option value="">All</option>
                        <option value="win50">&gt;= 50% Win</option>
                        <option value="loss50">&lt; 50% Win</option>
                      </select>
                    </th>
                    <th>
                      <select
                        value={colFilterMean}
                        onChange={(e) => setColFilterMean(e.target.value)}
                      >
                        <option value="">All</option>
                        <option value="pos">Positive</option>
                        <option value="neg">Negative</option>
                      </select>
                    </th>
                    <th>
                      <div className="d-flex gap-1 align-items-center">
                        <select
                          value={colFilterExcess}
                          onChange={(e) => setColFilterExcess(e.target.value)}
                        >
                          <option value="">All</option>
                          <option value="pos">Outperformed</option>
                          <option value="neg">Underperformed</option>
                        </select>
                        {hasActiveFilters && (
                          <button
                            type="button"
                            className="btn btn-default btn-xs btn-clear-filter"
                            onClick={clearFilters}
                            title="Clear filters"
                            style={{ width: 'auto', whiteSpace: 'nowrap' }}
                          >
                            Clear
                          </button>
                        )}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMonthly.length > 0 ? (
                    filteredMonthly.map((m, idx) => (
                      <tr key={idx}>
                        <td className="font-weight-bold">{m.month}</td>
                        <td className="text-end">{m.signals}</td>
                        <td className="text-end font-weight-bold">
                          <span
                            className={
                              (toNum(m.winRatePct) ?? 0) >= 50 ? 'pr-positive' : 'pr-negative'
                            }
                          >
                            {formatPercent(m.winRatePct, { showPlus: false })}
                          </span>
                        </td>
                        <td className="text-end">
                          {formatPercent(m.meanPct, { showPlus: true })}
                        </td>
                        <td
                          className={`text-end ${
                            (toNum(m.meanExcessPct) ?? 0) >= 0 ? 'pr-positive' : 'pr-negative'
                          }`}
                        >
                          {formatPercent(m.meanExcessPct, { showPlus: true })}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center py-3 text-muted">
                        No monthly records match the active filter criteria.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </DoubleScrollTable>
          </div>
        </div>
      )}
    </div>
  );
};

export default StrategyBacktest;
